import { useState, useEffect, useRef } from 'react';
import { useStore } from '@/redux/store';
import sitemap from './list.json';
import {
  Accordion,
  ContentSectionHeader,
  Hyperlink,
  List,
  PageHeader,
  Typography,
  VerticalTabs,
} from 'br2s-ui-componentlibrary_r2_02';
import { useNavigate } from 'react-router-dom';
import Container from '@/components/Container/Container';
import './SiteMap.scss';
import { scrollToElement } from '@/utilities/utilities';

type PageSubheaderItem = {
  label: string;
  items: any[];
};

const SiteMap = () => {
  const navigate = useNavigate();
  const { auth } = useStore();
  const [activeIndex, setActiveIndex] = useState(0);
  const selected = sitemap.SITE_MAP_CONTENT[activeIndex];
  const [activeSubTab, setActiveSubTab] = useState(0);
  const isFirstRender = useRef(true);

  // istanbul ignore next
  useEffect(() => {
    if (!isFirstRender.current) {
      scrollToElement(`sub-header-container-${activeSubTab}`);
    }

    setTimeout(() => {
      isFirstRender.current = false;
    }, 1000);
  }, [activeSubTab]);

  useEffect(() => {
    if (!isFirstRender.current) {
      scrollToElement(`site-map-tabs-content`);
    }
  }, [activeIndex]);

  const checkReqLogin = (required: boolean) => required && !auth.authenticated;

  const handleLogin = (url: string) => {
    document.dispatchEvent(
      new CustomEvent('requestLogin', { detail: { url } }),
    );
  };

  // istanbul ignore next
  const renderHyperlink = (
    label: string,
    url: string,
    requiresLogin: boolean,
    statedURL = false,
    deprioUrl = null,
    deprioFlag = '',
  ) => {
    const getUrl = () => {
      if (deprioUrl && deprioFlag !== 'true')
        return `${process.env.REACT_APP_SHELL_MFE_URL}${deprioUrl}`;

      if (url === 'REACT_APP_REQUEST_PERMISSION_DISCLOSURE_URL')
        return process.env.REACT_APP_REQUEST_PERMISSION_DISCLOSURE_URL ?? '';

      if (statedURL) return url;

      return `${process.env.REACT_APP_SHELL_MFE_URL}${url}`;
    };

    return checkReqLogin(requiresLogin) ? (
      <div onClick={() => handleLogin(url)}>
        <Hyperlink
          id="transaction-link"
          fontStyle="base"
          label={label}
          href="#"
        />
      </div>
    ) : (
      <Hyperlink
        id="transaction-link"
        fontStyle="base"
        label={label}
        href={getUrl()}
      />
    );
  };

  const renderSubCategory = (subCategory: any[]) =>
    subCategory.map((item) => ({
      text: renderHyperlink(
        item.label,
        item.url,
        item.requiredLogin,
        item.statedURL,
        item.deprioUrl,
        item.deprioFlag,
      ),
      child: renderChildren(item.child),
    }));

  const renderChildren = (
    children: any[] = [],
  ): Array<{ text: JSX.Element; child: any[] }> =>
    children.map((childItem) => ({
      text: renderHyperlink(
        childItem.label,
        childItem.url,
        childItem.requiredLogin,
        childItem.statedURL,
        childItem.deprioUrl,
        childItem.deprioFlag,
      ),
      child: renderChildren(childItem.child),
    }));

  const renderCategoryItems = (categoryItems: any[]) =>
    categoryItems.map((subCategory: any) => (
      <div key={subCategory.label} className="transaction-container">
        {subCategory.label && (
          <Typography
            type="subheadline"
            label={subCategory.label}
            weight="bold"
          />
        )}
        <List
          id="transaction-list"
          data={renderSubCategory(subCategory.links)}
          type="bulleted"
          maxList={100}
        />
      </div>
    ));

  const renderAccordionItems = (items: any[]) =>
    items.map((item: { category: string; categoryItems: string[] }) => ({
      isDefaultExpanded: true,
      headerTitle: item.category,
      children: (
        <div key={item.category} className="category-item">
          {renderCategoryItems(item.categoryItems)}
        </div>
      ),
    }));

  const transformPageSubheaderToSubTabs = (
    pageSubheader: PageSubheaderItem[],
  ) => {
    return pageSubheader.map((item, index) => ({
      label: item.label,
      isActive: false,
      onClick: () => setActiveSubTab(index),
      id: index,
    }));
  };

  const renderSiteMapContent = (selected: any) => (
    <div
      className="site-map-tabs-content"
      id="site-map-tabs-content"
      key={`${selected.pageHeader}-${selected.id}`}
    >
      <Typography type="headline-4" label={selected.pageHeader} weight="bold" />
      {selected.pageSubheader.map(
        (psubHeader: { label: string; items: any }, index: number) => (
          <div
            className="sub-header-container"
            id={`sub-header-container-${index}`}
            key={`${selected.pageHeader}-${selected.id}`}
          >
            {psubHeader.label && (
              <ContentSectionHeader
                id="pagesb-title"
                headerLabel={psubHeader.label}
                level={3}
              />
            )}
            <Accordion
              id="site-map-accordion"
              items={renderAccordionItems(psubHeader.items)}
              helperButtonPosition="both"
            />
          </div>
        ),
      )}
    </div>
  );

  return (
    <div className="site-map-container">
      <PageHeader
        id="page-header"
        title="Sitemap"
        description="See the pages available on Bizfile website here."
        breadcrumbData={[
          { label: 'Home', onClick: () => navigate('/') },
          { label: 'Sitemap' },
        ]}
      />
      <Container>
        <div className="content-container container">
          <div className="sitem-page-content">
            <VerticalTabs
              id="site-map-vertical-tab"
              isHaveBorder={false}
              activeSubIndex={activeSubTab}
              tabs={sitemap.SITE_MAP_CONTENT.map(
                (
                  item: {
                    pageHeader: string;
                    pageSubheader: PageSubheaderItem[];
                  },
                  index: number,
                ) => ({
                  id: index,
                  label:
                    item.pageHeader === 'Account and general'
                      ? 'General'
                      : item.pageHeader,
                  onClick: () => setActiveIndex(index),
                  subTabs:
                    item.pageSubheader.length > 1
                      ? transformPageSubheaderToSubTabs(item.pageSubheader)
                      : undefined,
                }),
              )}
              activeIndex={activeIndex}
              children={renderSiteMapContent(selected)}
            />
          </div>
        </div>
      </Container>
    </div>
  );
};

export default SiteMap;
